import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/inputs/Card';
import { Row, Col } from 'react-bootstrap';
import { Formik, Form, getIn } from 'formik';
import TextInput from 'components/inputs/Input';
import { MdClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import * as Yup from 'yup';
import { CREATE_REPORT_DOWNLOAD_REQUEST, GET_COMPANIES_BY_MODE } from 'actions/downloadAdvanceReport';
import { useTranslation } from 'react-i18next';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { toast } from 'react-toastify';
import Select from 'components/inputs/Select';
import { DEFAULT_LIMIT } from 'components/common/constant';

const DownloadAllDataReportForm = ({ onClose = _.noop(), downloadReport = _.noop() }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const AdvanceReportSchema = Yup.object().shape({
    name: Yup.string().required(t('downloadAllDataReportForm.name')),
    filter: Yup.object().shape({
      from: Yup.string().required(t('downloadAllDataReportForm.from')),
      to: Yup.string().required(t('downloadAllDataReportForm.to')),
      mode: Yup.array().min(1, 'Please select at least one type').required('Mode is required!'),
    }),
  });

  const allCompaniesByMode = useSelector((state) => state.companiesByMode.companiesByMode);

  const mode = [
    { label: 'B2B', value: 'b2b' },
    { label: 'B2C', value: 'b2c' },
    { label: 'Hybrid', value: 'hybrid' },
    { label: 'SaaS', value: 'SaaS' },
  ];

  const getCompaniesByMode = useCallback((data) => {
    dispatch({
      type: GET_COMPANIES_BY_MODE.REQUEST,
      payload: data,
    });
  }, []);

  const [fromDate, setFromDate] = useState('');

  const initialValues = {
    name: '',
    filter: {
      from: '',
      to: '',
      mode: [],
      tenant_id: [],
    },
  };

  const createReportDownloadRequest = useCallback((data) => {
    dispatch({
      type: CREATE_REPORT_DOWNLOAD_REQUEST.REQUEST,
      payload: data,
      cb: (res) => {
        if (res) {
          if (_.get(res, 'status') === 200 || _.get(res, 'status') === 201) {
            toast.success('Report generation request submitted successfully');
            setTimeout(() => {
              downloadReport({});
            }, 500);
          } else {
            toast.error('Failed to create report generation request');
          }
        } else {
          toast.error('Failed to create report generation request');
        }
      },
    });
  }, []);

  const handleFromDateChange = (val, setFieldValue) => {
    const dateValue = val.target.value;
    setFromDate(dateValue);
    setFieldValue('filter.from', dateValue);
  };

  return (
    <React.Fragment>
      <div className="advance-report-form-page__main">
        <Card>
          <div className="advance-report-form-header__block">
            &nbsp;
            <div className="advance-report-header-name">
              <div className="advance-report-form-text-align">{t('downloadAllDataReportForm.title')}</div>
            </div>
            <div className="advance-close-btn">
              <MdClose size={30} color="#be210b" onClick={onClose} />
            </div>
            &nbsp;
          </div>
          <div className="advance-report-form-body__block">
            <div className="advance-report-form--block">
              <Formik
                initialValues={initialValues}
                validationSchema={AdvanceReportSchema}
                onSubmit={(values, { setSubmitting }) => {
                  // Handle tenant_ids logic
                  let tenant_ids = [];

                  if (values.filter.tenant_id && values.filter.tenant_id.length > 0) {
                    // If specific tenants are selected, use their IDs
                    tenant_ids = values.filter.tenant_id.map((tenant) => {
                      // Check if tenant is already an ID string or an object with value property
                      return typeof tenant === 'string' ? tenant : tenant.value;
                    });
                  } else {
                    // If no tenants selected, get all tenant IDs from the current mode selection
                    if (allCompaniesByMode && allCompaniesByMode.length > 0) {
                      tenant_ids = allCompaniesByMode.map((company) => {
                        return company.id || company.value || company._id;
                      });
                    }
                  }

                  const reportData = {
                    name: values.name,
                    filter: {
                      from: moment(values.filter.from).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
                      to: moment(values.filter.to).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
                      mode: values.filter.mode,
                      tenant_ids: tenant_ids,
                    },
                    is_all_data: true,
                  };

                  setTimeout(() => {
                    createReportDownloadRequest(reportData);
                    setSubmitting(false);
                    onClose();
                  }, 500);
                }}
              >
                {({ values, handleSubmit, setFieldValue, handleChange, errors, touched }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col lg={12}>
                        <TextInput
                          isRequired
                          label={t('downloadAllDataReportForm.reportName')}
                          placeholder={t('downloadAllDataReportForm.enterName')}
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          error={getIn(errors, 'name') && getIn(touched, 'name') ? getIn(errors, 'name') : null}
                        />
                      </Col>
                      <Col lg={6}>
                        <span className="font-color">{t('downloadAllDataReportForm.startDateTime')}</span>
                        <div>&nbsp;</div>
                        <div className="time_sec">
                          <span className="custome_time">
                            <input
                              className="ct_selecter"
                              type="date"
                              value={values.filter.from}
                              max={moment().format('YYYY-MM-DD')}
                              onChange={(val) => handleFromDateChange(val, setFieldValue)}
                            />
                            <div style={{ color: '#dc4949', fontSize: 10 }}>
                              {getIn(errors, 'filter.from') && getIn(touched, 'filter.from') ? getIn(errors, 'filter.from') : null}
                            </div>
                          </span>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <span className="font-color">{t('downloadAllDataReportForm.endDateTime')}</span>
                        <div>&nbsp;</div>
                        <div className="time_sec">
                          <span className="custome_time">
                            <input
                              className="ct_selecter"
                              disabled={!fromDate}
                              type="date"
                              placeholder={t('downloadAllDataReportForm.selectDateTime')}
                              value={values.filter.to || ''}
                              min={fromDate}
                              max={moment().format('YYYY-MM-DD')}
                              onChange={(val) => {
                                setFieldValue(`filter.to`, val.target.value);
                              }}
                            />
                            <div style={{ color: '#dc4949', fontSize: 10 }}>
                              {getIn(errors, 'filter.to') && getIn(touched, 'filter.to') ? getIn(errors, 'filter.to') : null}
                            </div>
                          </span>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <Select
                          isRequired
                          label={t('downloadAllDataReportForm.mode')}
                          options={
                            !_.isEmpty(mode) && [
                              ..._.map(mode, (item) => {
                                return { label: item.label, value: item.value };
                              }),
                            ]
                          }
                          placeholder={t('downloadAllDataReportForm.selectType')}
                          name="filter.mode"
                          isMulti
                          value={values.filter.mode}
                          onMenuScrollDown={true}
                          onChange={(val) => {
                            getCompaniesByMode({ mode: val, limit: DEFAULT_LIMIT });
                            setFieldValue('filter.mode', val);
                          }}
                          error={getIn(errors, 'filter.mode') && getIn(touched, 'filter.mode') ? getIn(errors, 'filter.mode') : null}
                        />
                      </Col>
                      <Col lg={6}>
                        <Select
                          label={t('downloadAllDataReportForm.tenantName')}
                          options={
                            Array.isArray(allCompaniesByMode) && allCompaniesByMode.length > 0
                              ? allCompaniesByMode.map((item) => ({
                                  label: item.label || item.name || item.company_name || 'Unknown',
                                  value: item.id || item.value || item._id,
                                }))
                              : []
                          }
                          placeholder={t('downloadAllDataReportForm.selectName')}
                          name="filter.tenant_id"
                          isMulti
                          value={values.filter.tenant_id}
                          onMenuScrollDown={true}
                          onChange={(val) => {
                            setFieldValue('filter.tenant_id', val);
                          }}
                        />
                      </Col>
                    </Row>
                    <div className="advance-report-input-box__block">
                      <Row>
                        <Col lg={6}>
                          <Button className="advance--btn cancel--btn" onClick={onClose}>
                            {t('button.cancel')}
                          </Button>
                        </Col>
                        <Col lg={6}>
                          <Button type="submit" className="advance--btn  remove--btn">
                            {t('downloadAllDataReportForm.button')}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </Card>
      </div>
    </React.Fragment>
  );
};
DownloadAllDataReportForm.propTypes = {
  onClose: PropTypes.func,
  downloadReport: PropTypes.func,
};
export default DownloadAllDataReportForm;
